import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Edit, Plus, UserRound, UserRoundX, X } from 'lucide-react';
import { gestionarTecnicos } from '../servicios/api';

const GestionTecnicosPage = () => {
  const navigate = useNavigate();
  const usuarioActual = JSON.parse(localStorage.getItem('usuario') || 'null');
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoEditando, setTecnicoEditando] = useState(null);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [telefonoWhatsapp, setTelefonoWhatsapp] = useState('');
  const [whatsappActivo, setWhatsappActivo] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const cargarTecnicos = useCallback(async () => {
    try {
      const respuesta = await gestionarTecnicos({ method: 'GET' });
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudieron cargar los técnicos.');
      setTecnicos(respuesta.tecnicos || []);
    } catch (loadError) {
      setError(loadError.response?.data?.mensaje || loadError.message || 'No se pudieron cargar los técnicos.');
    }
  }, []);

  useEffect(() => {
    if (usuarioActual?.rol !== 'administrador') {
      navigate('/dashboard', { replace: true });
      return;
    }
    cargarTecnicos();
  }, [cargarTecnicos, navigate, usuarioActual?.rol]);

  const limpiarFormulario = () => {
    setTecnicoEditando(null);
    setNombre('');
    setEmail('');
    setPassword('');
    setTelefonoWhatsapp('');
    setWhatsappActivo(false);
  };

  const editarTecnico = (tecnico) => {
    setTecnicoEditando(tecnico);
    setNombre(tecnico.nombre);
    setEmail(tecnico.email);
    setPassword('');
    setTelefonoWhatsapp(tecnico.telefono_whatsapp || '');
    setWhatsappActivo(Number(tecnico.whatsapp_avisos_activos) === 1);
    setError('');
    setMensaje('');
  };

  const guardarTecnico = async (event) => {
    event.preventDefault();
    setCargando(true);
    setError('');
    setMensaje('');
    try {
      const respuesta = await gestionarTecnicos({
        method: 'POST',
        data: {
          accion: tecnicoEditando ? 'actualizar' : 'crear',
          tecnico_id: tecnicoEditando?.tecnico_id,
          nombre,
          email,
          password,
          telefono_whatsapp: telefonoWhatsapp,
          whatsapp_avisos_activos: whatsappActivo,
        },
      });
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudo guardar el técnico.');
      setMensaje(respuesta.mensaje);
      limpiarFormulario();
      await cargarTecnicos();
    } catch (saveError) {
      setError(saveError.response?.data?.mensaje || saveError.message || 'No se pudo guardar el técnico.');
    } finally {
      setCargando(false);
    }
  };

  const cambiarActivo = async (tecnico) => {
    setError('');
    try {
      const respuesta = await gestionarTecnicos({
        method: 'POST',
        data: { accion: tecnico.activo ? 'desactivar' : 'activar', tecnico_id: tecnico.tecnico_id },
      });
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudo actualizar el estado.');
      await cargarTecnicos();
    } catch (statusError) {
      setError(statusError.response?.data?.mensaje || statusError.message || 'No se pudo actualizar el estado.');
    }
  };

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <section>
          <header className="mb-6 border-b border-slate-800 pb-4">
            <p className="text-xs font-semibold uppercase text-orange-300">Administración</p>
            <h1 className="mt-1 text-3xl font-bold">Técnicos</h1>
          </header>
          {error && <p role="alert" className="mb-4 rounded border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-200">{error}</p>}
          {mensaje && <p role="status" className="mb-4 rounded border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200">{mensaje}</p>}
          <div className="divide-y divide-slate-800 rounded-md border border-slate-800 bg-slate-900">
            {tecnicos.length === 0 ? <p className="p-5 text-sm text-slate-400">No hay técnicos registrados.</p> : tecnicos.map((tecnico) => (
              <article key={tecnico.tecnico_id} className="flex flex-wrap items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <h2 className="break-words font-semibold">{tecnico.nombre}</h2>
                  <p className="break-all text-sm text-slate-400">{tecnico.email}</p>
                  {tecnico.telefono_whatsapp && (
                    <p className="mt-1 text-sm text-slate-400">
                      WhatsApp: {tecnico.telefono_whatsapp}
                      {Number(tecnico.whatsapp_avisos_activos) ? ' · Avisos activos' : ' · Sin consentimiento'}
                    </p>
                  )}
                  <span className={`mt-2 inline-flex rounded px-2 py-1 text-xs ${Number(tecnico.activo) ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
                    {Number(tecnico.activo) ? 'Activo' : 'Desactivado'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => editarTecnico(tecnico)} aria-label={`Editar ${tecnico.nombre}`} className="rounded border border-slate-700 p-2 hover:bg-slate-800"><Edit size={17} /></button>
                  <button type="button" onClick={() => cambiarActivo(tecnico)} aria-label={Number(tecnico.activo) ? `Desactivar ${tecnico.nombre}` : `Activar ${tecnico.nombre}`} className="rounded border border-slate-700 p-2 hover:bg-slate-800">
                    {Number(tecnico.activo) ? <UserRoundX size={17} /> : <UserRound size={17} />}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <aside className="h-fit rounded-md border border-slate-800 bg-slate-900 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold">{tecnicoEditando ? 'Editar técnico' : 'Nuevo técnico'}</h2>
            {tecnicoEditando && <button type="button" onClick={limpiarFormulario} aria-label="Cancelar edición" className="rounded p-1 text-slate-400 hover:bg-slate-800"><X size={18} /></button>}
          </div>
          <form onSubmit={guardarTecnico} className="space-y-4">
            <label className="block text-sm text-slate-300">Nombre
              <input required value={nombre} onChange={(event) => setNombre(event.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-white" />
            </label>
            <label className="block text-sm text-slate-300">Email
              <input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-white" />
            </label>
            <label className="block text-sm text-slate-300">Teléfono WhatsApp del técnico
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+34600111222"
                value={telefonoWhatsapp}
                onChange={(event) => setTelefonoWhatsapp(event.target.value)}
                className="mt-1 w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-white"
              />
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={whatsappActivo}
                onChange={(event) => setWhatsappActivo(event.target.checked)}
                className="mt-1"
              />
              <span>El técnico ha dado su consentimiento para recibir por WhatsApp nuevas asignaciones y recordatorios de servicios.</span>
            </label>
            <p className="text-xs text-slate-500">
              El teléfono debe incluir el prefijo internacional. Solo se enviarán avisos si esta casilla está activada.
            </p>
            <label className="block text-sm text-slate-300">{tecnicoEditando ? 'Nueva contraseña (opcional)' : 'Contraseña'}
              <input required={!tecnicoEditando} minLength="8" type="password" value={password} onChange={(event) => setPassword(event.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-white" />
            </label>
            <button type="submit" disabled={cargando} className="inline-flex w-full items-center justify-center gap-2 rounded bg-orange-600 px-4 py-2 font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
              {tecnicoEditando ? <Check size={17} /> : <Plus size={17} />}
              {cargando ? 'Guardando...' : tecnicoEditando ? 'Guardar cambios' : 'Crear técnico'}
            </button>
          </form>
        </aside>
      </div>
    </main>
  );
};

export default GestionTecnicosPage;
